import { useEmailTimeZone } from "../useEmailTimeZone";
import { ApolloError } from "@apollo/client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  useCreateCmpNewsletterMutation,
  useArchiveCmpNewsletterRecipientGroupMutation,
  useGetCmpManageMembersQuery,
  useGetCmpNewsletterRecipientGroupsQuery,
  usePreviewCmpNewsletterRecipientsLazyQuery,
  usePublishCmpNewsletterToNewsroomMutation,
  useSendCmpNewsletterNowMutation,
  useUpdateCmpNewsletterMutation,
} from "../../../../graphql/types";
import { useCmpEmailSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  EMAIL_CHAMBER_LOADING_MESSAGE,
  emailLoadErrorText,
  EMAIL_OPERATION_ERROR_CONTEXT,
  assertEmailBuilderDraftPersisted,
  emailFormStateToInput,
  newsletterReturnToFormState,
  previewQueryToSummary,
  recipientGroupReturnToUiGroup,
} from "../emailBackendMappers";
import EmailForm from "../EmailForm";
import { useNewsletterDeliveryActions } from "../useNewsletterDeliveryActions";
import { useUnpublishNewsletterFromNewsroom } from "../useUnpublishNewsletterFromNewsroom";
import { getEnabledNewsletterSenderEmails } from "../emailSenderSelection";
import {
  EmailFormState,
  EmailAudienceMemberOption,
  buildDefaultCreateEmailForm,
  cloneEmailForm,
} from "../emailMockData";
import { applyCmpOrganizationNoun } from "../../../../utils/cmpOrganizationNoun";
import { UserFacingError } from "../../../../utils/userFacingError";

type InitialEmailSnapshot = {
  chamberId: string;
  email: EmailFormState;
};

const CreateEmail = () => {
  const navigate = useNavigate();
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const { timeZone, error: timeZoneError, refetch: refetchTimeZone } = useEmailTimeZone(chamberId);
  const createdDraftRef = useRef<{ chamberId: string; id: string } | null>(
    null,
  );
  const [initialEmailSnapshot, setInitialEmailSnapshot] =
    useState<InitialEmailSnapshot | null>(null);
  // Design (16): Publish to Newsroom also works on a new email. EmailForm
  // saves it first, so the action always has the saved id.
  const [publishCmpNewsletterToNewsroom] =
    usePublishCmpNewsletterToNewsroomMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const { unpublishFromNewsroom } = useUnpublishNewsletterFromNewsroom(chamberId);
  const publishNewsroom = async (email: EmailFormState) => {
    if (!chamberId || !email.id) {
      throw new UserFacingError("Save the email before you publish it to the newsroom.");
    }
    const result = await publishCmpNewsletterToNewsroom({
      variables: { input: { chamberId, newsletterId: email.id } },
    });
    const saved = result.data?.publishCmpNewsletterToNewsroom;
    if (saved?.newsletterSavedSuccessfully !== true || !saved.newsletter) {
      throw new Error("The newsroom record could not be published.");
    }
    return newsletterReturnToFormState(saved.newsletter, timeZone || "");
  };
  const unpublishNewsroom = async (email: EmailFormState) =>
    newsletterReturnToFormState(await unpublishFromNewsroom(email.id), timeZone || "");

  const {
    data: settingsData,
    error: settingsError,
    loading: settingsLoading,
    refetch: refetchSettings,
  } = useCmpEmailSettings(
    chamberId,
    "network-only",
    EMAIL_OPERATION_ERROR_CONTEXT,
  );
  const initialEmailSettings = settingsData?.getChamberMemberProSettings.email;
  const senderEmailOptions = useMemo(
    () => getEnabledNewsletterSenderEmails(initialEmailSettings || {}),
    [initialEmailSettings],
  );
  const {
    data: groupsData,
    error: groupsError,
    refetch: refetchGroups,
  } = useGetCmpNewsletterRecipientGroupsQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const {
    data: membersData,
    error: membersError,
    loading: membersLoading,
    refetch: refetchMembers,
  } = useGetCmpManageMembersQuery({
    // The audience picker needs member rows only (no invoice read).
    variables: { input: { chamberId, includeInvoiceSummary: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const [createCmpNewsletter, { loading: creatingNewsletter }] =
    useCreateCmpNewsletterMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [updateCmpNewsletter, { loading: updatingNewsletter }] =
    useUpdateCmpNewsletterMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  const [archiveCmpNewsletterRecipientGroup] =
    useArchiveCmpNewsletterRecipientGroupMutation({
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const [previewCmpNewsletterRecipients] =
    usePreviewCmpNewsletterRecipientsLazyQuery({
      fetchPolicy: "network-only",
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const [sendCmpNewsletterNow, { loading: sendingNow }] =
    useSendCmpNewsletterNowMutation({ context: EMAIL_OPERATION_ERROR_CONTEXT });
  // D-EM6: Schedule and Send Test Email work before the first save. EmailForm
  // saves first (create, then update of the same id); these use that id.
  const deliveryActions = useNewsletterDeliveryActions({
    chamberId,
    timeZone,
    getNewsletterId: (email) => email.id || undefined,
  });

  useEffect(() => {
    if (!chamberId || settingsLoading || !initialEmailSettings) return;

    setInitialEmailSnapshot((currentSnapshot) => {
      // New-email defaults are a one-time seed. Later Apollo cache refreshes
      // must not replace anything the user has already changed in the form.
      if (currentSnapshot?.chamberId === chamberId) return currentSnapshot;

      return {
        chamberId,
        email: buildDefaultCreateEmailForm({
          chamberLogo: chamber?.logo,
          chamberName: chamber?.name,
          replyToEmail: initialEmailSettings.replyToEmail,
          senderEmail: senderEmailOptions[0],
        }),
      };
    });
  }, [
    chamber?.logo,
    chamber?.name,
    chamberId,
    initialEmailSettings,
    senderEmailOptions,
    settingsLoading,
  ]);

  const initialEmail =
    initialEmailSnapshot?.chamberId === chamberId
      ? initialEmailSnapshot.email
      : null;
  const recipientGroups = useMemo(
    () =>
      (groupsData?.getCmpNewsletterRecipientGroups.groups || [])
        .filter((group) => !group.isArchived)
        .map(recipientGroupReturnToUiGroup),
    [groupsData?.getCmpNewsletterRecipientGroups.groups],
  );
  const memberOptions: EmailAudienceMemberOption[] = useMemo(
    () =>
      (membersData?.getCmpManageMembers.rows || []).map((member) => ({
        id: member.id,
        companyName: member.companyName,
        companyLogo: member.companyLogo,
        categoryLabels: member.categoryLabels,
        lifecycleStatus: member.lifecycleStatus,
        membershipTypeId: member.membershipTypeId || "",
        membershipTypeName: member.membershipTypeName || "",
        dateJoinedUtcMilli: member.dateJoinedUtcMilli,
        fullTimeEmployees: member.fullTimeEmployees,
        partTimeEmployees: member.partTimeEmployees,
        renewalDateUtcMilli: member.renewalDateUtcMilli,
        categoryId: member.categoryId,
      })),
    [membersData?.getCmpManageMembers.rows],
  );
  const handleSendSuccess = useCallback(() => {
    navigate(RoutePaths.chamberAdmin_emails_manage, { replace: true });
  }, [navigate]);

  const submitEmail = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun),
      );
    }

    const newsletterId =
      email.id ||
      (createdDraftRef.current?.chamberId === chamberId
        ? createdDraftRef.current.id
        : undefined);
    const data = emailFormStateToInput({ ...email, id: newsletterId, timeZone: timeZone || "" }, chamber);
    // A failed send leaves the newly saved draft on this route. Save retries
    // must update that draft instead of creating another record.
    const saveResult = newsletterId
      ? (
          await updateCmpNewsletter({
            variables: {
              input: { chamberId, newsletterId, data },
            },
          })
        ).data?.updateCmpNewsletter
      : (
          await createCmpNewsletter({
            variables: {
              input: {
                chamberId,
                data,
              },
            },
          })
        ).data?.createCmpNewsletter;
    const savedEmail = saveResult?.newsletter;

    if (saveResult?.newsletterSavedSuccessfully !== true || !savedEmail?.id) {
      throw new Error("The email draft could not be saved.");
    }

    // Keep the confirmed identity even if the content readback check fails.
    // A retry must repair this draft, without creating a second record.
    createdDraftRef.current = { chamberId, id: savedEmail.id };
    const savedFormEmail = newsletterReturnToFormState(savedEmail, timeZone || "");
    assertEmailBuilderDraftPersisted({
      submittedEmail: email,
      savedEmail: savedFormEmail,
    });

    // Design: Save Draft keeps the user on Create Email with the success
    // pop-up. The saved id above makes every later save update this draft.
    return savedFormEmail;
  };

  const sendNow = async (email: EmailFormState) => {
    if (!chamberId || !email.id) {
      throw new UserFacingError(applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun));
    }

    const result = await sendCmpNewsletterNow({
      variables: {
        input: {
          chamberId,
          newsletterId: email.id,
          note: email.reviewNote || undefined,
        },
      },
    });
    const sendResult = result.data?.sendCmpNewsletterNow;
    const sentEmail = sendResult?.newsletter;

    if (!sendResult?.newsletterSendQueuedSuccessfully || !sentEmail) {
      throw new UserFacingError(
        "We could not confirm that sending started. Open Manage Emails & Communications and check the status of this email before you try again.",
      );
    }

    return newsletterReturnToFormState(sentEmail, timeZone || "");
  };

  const archiveRecipientGroup = async (groupId: string) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun),
      );
    }

    const result = await archiveCmpNewsletterRecipientGroup({
      variables: { input: { chamberId, groupId } },
    });
    if (
      !result.data?.archiveCmpNewsletterRecipientGroup?.groupSavedSuccessfully
    ) {
      throw new Error("The custom audience list could not be archived.");
    }
    // Apollo applies the returned group's isArchived value to the cached list.
    // A refresh failure must not report a confirmed archive as a failure.
    await Promise.allSettled([refetchGroups()]);
  };

  const previewRecipients = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun),
      );
    }

    const result = await previewCmpNewsletterRecipients({
      variables: {
        input: {
          chamberId,
          recipientSelection: emailFormStateToInput({ ...email, timeZone: timeZone || "" }, chamber)
            .recipientSelection!,
        },
      },
    });

    if (result.error) throw result.error;
    if (result.errors?.length)
      throw new ApolloError({ graphQLErrors: result.errors });
    if (!result.data?.previewCmpNewsletterRecipients) {
      throw new Error("The recipient preview could not be loaded.");
    }
    return previewQueryToSummary(result.data.previewCmpNewsletterRecipients);
  };

  const lookupErrors = [
    { error: timeZoneError, message: emailLoadErrorText(`your ${noun.singular} time zone`), retry: refetchTimeZone },
    {
      error: settingsError,
      message: emailLoadErrorText(`your ${noun.singular} email settings`),
      retry: refetchSettings,
    },
    {
      error: groupsError,
      message: emailLoadErrorText("your custom audiences"),
      retry: refetchGroups,
    },
    {
      error: membersError,
      message: emailLoadErrorText("your member list"),
      retry: refetchMembers,
    },
  ].filter(({ error }) => error);
  const lookupErrorNotices = lookupErrors.map(({ message, retry }) => (
    <div
      key={message}
      className="mb-4 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700"
      role="alert"
    >
      {message}
      <button
        type="button"
        className="ml-3 min-h-[44px] px-2 underline"
        onClick={() => {
          void retry().catch(() => undefined);
        }}
      >
        Try again
      </button>
    </div>
  ));

  if (settingsError && !initialEmail) {
    return <>{lookupErrorNotices}</>;
  }

  if (!initialEmail) {
    return (
      <div
        className="rounded-md border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600"
        role="status"
      >
        Loading your email...
      </div>
    );
  }

  return (
    <>
      {lookupErrorNotices}
      <EmailForm
        key={chamberId}
        mode="create"
        timeZone={timeZone}
        initialEmail={cloneEmailForm(initialEmail)}
        recipientGroups={recipientGroups}
        memberOptions={memberOptions}
        memberOptionsLoading={membersLoading}
        senderEmailOptions={senderEmailOptions}
        isSaving={
          creatingNewsletter ||
          updatingNewsletter ||
          sendingNow ||
          deliveryActions.isRunning
        }
        onSubmit={submitEmail}
        onArchiveRecipientGroup={archiveRecipientGroup}
        onPreviewRecipients={previewRecipients}
        onSendNow={sendNow}
        onPublishNewsroom={publishNewsroom}
        onUnpublishNewsroom={unpublishNewsroom}
        onSendSuccess={handleSendSuccess}
        onPreviewTestSend={deliveryActions.previewTestSend}
        onSendReview={deliveryActions.sendReview}
        onSchedule={deliveryActions.scheduleEmail}
        onCancelSchedule={deliveryActions.cancelSchedule}
      />
    </>
  );
};

export default CreateEmail;
